/**
 * SubmitArticleModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A modal component for submitting articles with:
 * - Comprehensive form validation for all required fields
 * - Rich text editor (ReactQuill) for article description
 * - Image upload functionality with preview capability
 * - Fullscreen capability for enhanced editing experience
 * - Email submission simulation with confirmation state
 * - Error handling and display for user feedback
 * - Keyboard navigation (Escape key support)
 * - Form reset functionality on modal close
 * - Responsive design for various screen sizes
 * 
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {boolean} allowFullscreen - Whether fullscreen mode is available
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useEffect, useState } from 'react';
import './Modal.css';
import './SubmitArticleModal.css';
import apiHelper from '../utils/apiHelper';
import { useProfile } from '../contexts/ProfileContext';


/**
 * SubmitArticleModal component for handling article submission requests
 * with rich text editing and image upload capabilities
 */
function SubmitArticleModal({ show, onClose, allowFullscreen = false }) {
    const { profile } = useProfile();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [name, setName] = useState('');
    const [firm, setFirm] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [image, setImage] = useState(null); // To store the uploaded image
    const [imagePreview, setImagePreview] = useState(null); // For image preview
    const [emailSent, setEmailSent] = useState(false);
    const [errors, setErrors] = useState({});

    // Toggles the fullscreen mode of the modal
    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    // Validate the form inputs, including image check
    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) newErrors.name = 'Name is required';
        if (!description.trim()) newErrors.description = 'Description is required';
        if (!firm.trim()) newErrors.firm = 'Firm is required';
        if (!image) newErrors.image = 'Image is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Returns true if no errors
    };

    // Handles the form submission process
    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('firm', firm);
            formData.append('title', title);
            formData.append('description', description);
            formData.append('image', image);

            setEmailSent(true); // Simulate email sent confirmation
            // Form data ready for backend API integration
        }
    };

    // Handles image selection and creates an image preview
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file)); // Create a preview
        }
    };

    // Handles closing the modal and resetting state
    const handleCancel = () => {
        onClose();
        setEmailSent(false); // Reset emailSent when modal is closed
        setImage(null); // Reset image
        setImagePreview(null); // Reset image preview
    };

    useEffect(() => {
        if (show && profile?.email) {
            apiHelper.logModalOpen(profile.email, 'SubmitArticleModal', 'Submit Article', 'ArticleSubmission');
        }
    }, [show, profile?.email]);

    // If modal is not shown, return null to avoid rendering
    if (!show) return null;

    return (
        <div id="submitArticleModal" className={`custom-modal ${isFullScreen ? 'fullscreen' : ''}`}>
            <div className="modal-content">
                <div className="modal-header">
                    <span id="modalTitle">Submit an Article</span>
                    <div>
                        {allowFullscreen && (
                            <span
                                className="modalfullscreen fullscreen-btn fas fa-expand" onClick={toggleFullScreen}
                            ></span>
                        )}
                        <span
                            className="modalclose pointer fas fa-times"
                            onClick={handleCancel}
                        ></span>
                    </div>
                </div>

                <div className="modal-body" >
                    <div className="container" style={{textAlign: 'left'}}>
                        We would be delighted to feature your articles on the Irish Funds Member Portal.<br/><br/>
                        Please get in touch with the <a title="Irish Funds team" href="mailto:memberportal@irishfunds.ie">Irish Funds team</a> to submit an article.
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SubmitArticleModal;
